import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient, API } from "@/lib/api";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Bot,
  Sparkles,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  CreditCard,
  ChevronLeft,
  Zap,
  Gift
} from "lucide-react";

const quickActions = [
  { id: "features", icon: Sparkles, label: "What can Viraly do?", prompt: "What features does Viraly offer?" },
  { id: "pricing", icon: CreditCard, label: "Pricing & plans", prompt: "Tell me about Viraly pricing and plans" },
  { id: "rewards", icon: Gift, label: "Loyalty & rewards", prompt: "Tell me about the loyalty program and referral rewards" },
  { id: "how", icon: Zap, label: "How does it work?", prompt: "How does Viraly work? Give me a quick overview" },
  { id: "help", icon: HelpCircle, label: "I need help", prompt: "I need help with something" }
];

const feedbackCategories = [
  { id: "bug", icon: Bug, label: "Report Bug", color: "text-red-400", description: "Something isn't working" },
  { id: "billing", icon: CreditCard, label: "Billing Issue", color: "text-yellow-400", description: "Payment or credits problem" },
  { id: "feature", icon: Lightbulb, label: "Feature Request", color: "text-blue-400", description: "Suggest an improvement" }
];

export default function ViraChatbot() {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [feedbackCategory, setFeedbackCategory] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatRef = useRef(null);
  const feedbackRef = useRef(null);
  
  // Credit summary state
  const [creditInfo, setCreditInfo] = useState(null);
  
  // Check if B2B portal or Admin panel - they have their own interfaces
  const isB2BPortal = location.pathname.startsWith('/enterprise/') || 
                      location.pathname.startsWith('/partners') || 
                      location.pathname.startsWith('/m/');
  
  // Hide on admin panel completely
  const isAdminPanel = location.pathname === '/bajrangbali' || 
                       location.pathname.startsWith('/bajrangbali');
  
  // Fetch credit summary
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await apiClient.get("/users/credit-summary");
        setCreditInfo(res.data);
      } catch (err) {
        // Fallback to user object
      }
    };
    if (user && isOpen && !isAdminPanel) fetchCredits();
  }, [user, isOpen, isAdminPanel]);

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0 && !isAdminPanel) {
      const postsRemaining = creditInfo?.total_posts ?? user?.posts_remaining ?? 0;
      const greeting = user 
        ? `Hi ${user.name?.split(' ')[0] || 'there'}! 👋 I'm Vira, your Viraly assistant. You have **${postsRemaining} posts** remaining on your **${user.plan || 'free trial'}** plan. How can I help you today?`
        : "Hi there! 👋 I'm Vira, your Viraly assistant. I can help you learn about our AI-powered social media automation platform. What would you like to know?";
      
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [isOpen, user, creditInfo, isAdminPanel]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isAdminPanel) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isAdminPanel]);
  
  // Listen for external event to open chat
  useEffect(() => {
    const handleOpenViraChat = () => {
      if (!isAdminPanel) setIsOpen(true);
    };
    window.addEventListener('openViraChat', handleOpenViraChat);
    return () => window.removeEventListener('openViraChat', handleOpenViraChat);
  }, [isAdminPanel]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close chat if clicking outside
      if (chatRef.current && !chatRef.current.contains(event.target)) {
        const chatButton = document.querySelector('[data-testid="chatbot-btn"]');
        const feedbackButton = document.querySelector('[data-testid="feedback-btn"]');
        if (chatButton && !chatButton.contains(event.target) && 
            feedbackButton && !feedbackButton.contains(event.target)) {
          setIsOpen(false);
        }
      }
      // Close feedback if clicking outside
      if (feedbackRef.current && !feedbackRef.current.contains(event.target)) {
        const feedbackButton = document.querySelector('[data-testid="feedback-btn"]');
        const chatButton = document.querySelector('[data-testid="chatbot-btn"]');
        if (feedbackButton && !feedbackButton.contains(event.target) &&
            chatButton && !chatButton.contains(event.target)) {
          setIsFeedbackOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: "user", content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const headers = { "Content-Type": "application/json" };
      const token = localStorage.getItem("viraly_token");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        session_id: sessionId
      }, { headers });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having trouble connecting. Please try again or email care@viraly.ca for help." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    sendMessage(action.prompt);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    
    setLoading(true);
    try {
      // For bugs and billing issues, create a support ticket
      if (feedbackCategory?.id === "bug" || feedbackCategory?.id === "billing") {
        await apiClient.post("/support/tickets", {
          category: feedbackCategory.id,
          message: feedbackText,
          priority: feedbackCategory.id === "billing" ? "high" : "medium"
        });
        
        setFeedbackSent(true);
        setFeedbackText("");
        setFeedbackCategory(null);
        
        // Show confirmation message
        setTimeout(() => {
          setFeedbackSent(false);
          setIsFeedbackOpen(false);
          alert("✅ Ticket submitted! Check your email for confirmation. We'll respond within 2 business days.");
        }, 1500);
      } else {
        // Feature requests go to regular feedback
        const endpoint = user ? "/feedback" : "/feedback/public";
        const params = new URLSearchParams({
          message: feedbackText,
          category: feedbackCategory?.id || "feature"
        });
        
        if (user) {
          await apiClient.post(`${endpoint}?${params}`);
        } else {
          await axios.post(`${API}${endpoint}?${params}`);
        }
        
        setFeedbackSent(true);
        setFeedbackText("");
        setFeedbackCategory(null);
        
        setTimeout(() => {
          setFeedbackSent(false);
          setIsFeedbackOpen(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Feedback error:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  const handleChatToggle = () => {
    setIsOpen(!isOpen);
    if (isFeedbackOpen) setIsFeedbackOpen(false);
  };

  const handleFeedbackToggle = () => {
    setIsFeedbackOpen(!isFeedbackOpen);
    if (isOpen) setIsOpen(false);
  };

  // Hide on B2B portals - they have their own Vira On-Call
  // Hide on Admin panel - not needed there
  if (isB2BPortal || isAdminPanel) {
    return null;
  }

  return (
    <div className="fixed bottom-20 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {/* Chat Window */}
        {isOpen && (
          <motion.div
            ref={chatRef}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="w-[380px] h-[520px] bg-viraly-card border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-viraly-gradient flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Vira</h3>
                  <p className="text-xs text-white/70">Your AI Assistant</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === "user"
                        ? "bg-viraly-gradient text-white rounded-br-md"
                        : "bg-white/5 text-viraly-light rounded-bl-md"
                    }`}
                  >
                    <p 
                      className="text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-viraly-orange" />
                      <span className="text-sm text-viraly-muted">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions - Two rows of compact chips */}
            {!loading && (
              <div className="flex-shrink-0 border-t border-white/5 pt-2 pb-2 px-4">
                <p className="text-xs text-viraly-muted mb-1.5">Quick actions:</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 hover:bg-viraly-orange/20 border border-white/10 hover:border-viraly-orange/30 transition-all text-[11px] whitespace-nowrap"
                    >
                      <action.icon className="w-3 h-3 text-viraly-orange" />
                      <span className="text-viraly-light">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-white/10 flex-shrink-0">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Vira anything..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-viraly-orange/50 transition-colors"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="p-2.5 bg-viraly-gradient rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Feedback Window */}
        {isFeedbackOpen && (
          <motion.div
            ref={feedbackRef}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="w-[340px] bg-viraly-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {feedbackCategory && (
                  <button 
                    onClick={() => setFeedbackCategory(null)}
                    className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-viraly-muted" />
                  </button>
                )}
                <h3 className="font-bold text-sm">Send Feedback</h3>
              </div>
              <button 
                onClick={() => setIsFeedbackOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-viraly-muted" />
              </button>
            </div>

            <div className="p-4">
              {feedbackSent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-8 text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-7 h-7 text-viraly-success" />
                  </div>
                  <h3 className="font-bold mb-1">Thank you!</h3>
                  <p className="text-viraly-muted text-sm">Your feedback helps us improve.</p>
                </motion.div>
              ) : !feedbackCategory ? (
                <div className="space-y-3">
                  <p className="text-sm text-viraly-muted mb-3">What type of feedback?</p>
                  {feedbackCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setFeedbackCategory(cat)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left"
                    >
                      <cat.icon className={`w-5 h-5 ${cat.color}`} />
                      <span className="font-medium text-sm">{cat.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5`}>
                    <feedbackCategory.icon className={`w-4 h-4 ${feedbackCategory.color}`} />
                    <span className="text-sm">{feedbackCategory.label}</span>
                  </div>
                  
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder={
                      feedbackCategory.id === "bug" 
                        ? "Describe the issue..." 
                        : feedbackCategory.id === "feature"
                        ? "Tell us about your idea..."
                        : "Share your thoughts..."
                    }
                    className="w-full h-32 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-viraly-orange/50 resize-none"
                  />
                  
                  <button
                    onClick={submitFeedback}
                    disabled={loading || !feedbackText.trim()}
                    className="w-full py-2.5 bg-viraly-gradient rounded-xl font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Buttons - Vira on top, Feedback below */}
      <div className="flex flex-col items-center gap-2">
        {/* Hint labels when closed */}
        {!isOpen && !isFeedbackOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-end gap-1 mr-1"
          >
            <span className="text-xs text-viraly-muted bg-viraly-card/80 px-2 py-1 rounded-lg backdrop-blur-sm">
              Chat with Vira
            </span>
          </motion.div>
        )}
        
        {/* Main Chatbot Button (Vira) */}
        <button
          onClick={handleChatToggle}
          className={`w-14 h-14 rounded-full shadow-lg transition-all flex items-center justify-center ${
            isOpen 
              ? "bg-viraly-orange" 
              : "bg-viraly-gradient hover:shadow-xl animate-glow-pulse"
          }`}
          title="Chat with Vira"
          data-testid="chatbot-btn"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <MessageCircle className="w-6 h-6 text-white" />
          )}
        </button>

        {/* Feedback Button (Below Vira) */}
        <div className="flex flex-col items-center">
          {!isOpen && !isFeedbackOpen && (
            <span className="text-[10px] text-viraly-muted mb-1">Feedback</span>
          )}
          <button
            onClick={handleFeedbackToggle}
            className={`w-10 h-10 rounded-full shadow-md transition-all flex items-center justify-center ${
              isFeedbackOpen 
                ? "bg-viraly-orange" 
                : "bg-viraly-card hover:bg-white/10 border border-white/10"
            }`}
            title="Send Feedback"
            data-testid="feedback-btn"
          >
            {isFeedbackOpen ? (
              <X className="w-4 h-4 text-white" />
            ) : (
              <MessageSquare className="w-4 h-4 text-viraly-muted" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
